/**
 * Create Vehicle Modal Component
 * Form for creating a new vehicle
 */

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { useCreateVehicle } from '../../../hooks/useVehicles';
import { createVehicleSchema, vehicleTypes } from '../../../schemas/vehicleSchema';
import type { CreateVehicleFormData } from '../../../schemas/vehicleSchema';
import { ApiError } from '../../../services/api';

interface CreateVehicleModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateVehicleModal({ open, onClose }: CreateVehicleModalProps) {
  const { mutateAsync: createVehicle, isPending } = useCreateVehicle();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<CreateVehicleFormData>({
    vehicleNumber: '',
    vehicleType: 'rolloff_truck',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '',
    licensePlate: '',
    odometer: 0,
    dotInspectionDate: new Date().toISOString().split('T')[0] as string,
    dotExpirationDate: '',
    notes: '',
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        vehicleNumber: '',
        vehicleType: 'rolloff_truck',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        vin: '',
        licensePlate: '',
        odometer: 0,
        dotInspectionDate: new Date().toISOString().split('T')[0] as string,
        dotExpirationDate: '',
        notes: '',
      });
      setErrors({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate with Zod
    const result = createVehicleSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          newErrors[String(err.path[0])] = err.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    try {
      await createVehicle(result.data);
      onClose();
    } catch (error) {
      // Handle API validation errors with field-level details
      if (error instanceof ApiError && error.details && error.details.length > 0) {
        const fieldErrors: Record<string, string> = {};
        error.details.forEach((detail) => {
          fieldErrors[detail.field] = detail.message;
        });
        setErrors(fieldErrors);
      } else {
        // Fallback to generic error message
        setErrors({ submit: error instanceof Error ? error.message : 'Failed to create vehicle' });
      }
    }
  };

  const updateField = (
    field: keyof CreateVehicleFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Vehicle</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Vehicle Number & Type */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.vehicleNumber}
                onChange={(e) => updateField('vehicleNumber', e.target.value.toUpperCase())}
                className={`mt-1 block w-full rounded-md border ${
                  errors.vehicleNumber ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="TRUCK-01"
              />
              {errors.vehicleNumber && (
                <p className="mt-1 text-sm text-red-500">{errors.vehicleNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.vehicleType}
                onChange={(e) => updateField('vehicleType', e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {vehicleTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Make & Model */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Make <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => updateField('make', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.make ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="Peterbilt"
              />
              {errors.make && <p className="mt-1 text-sm text-red-500">{errors.make}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => updateField('model', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.model ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="348"
              />
              {errors.model && <p className="mt-1 text-sm text-red-500">{errors.model}</p>}
            </div>
          </div>

          {/* Year & Odometer */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Year <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => updateField('year', parseInt(e.target.value))}
                className={`mt-1 block w-full rounded-md border ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.year && <p className="mt-1 text-sm text-red-500">{errors.year}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Odometer (miles) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.odometer}
                onChange={(e) => updateField('odometer', parseInt(e.target.value))}
                className={`mt-1 block w-full rounded-md border ${
                  errors.odometer ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.odometer && <p className="mt-1 text-sm text-red-500">{errors.odometer}</p>}
            </div>
          </div>

          {/* VIN & License Plate */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                VIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => updateField('vin', e.target.value.toUpperCase())}
                maxLength={17}
                className={`mt-1 block w-full rounded-md border ${
                  errors.vin ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="1XPBDP9X3ND123456"
              />
              {errors.vin && <p className="mt-1 text-sm text-red-500">{errors.vin}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                License Plate <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.licensePlate}
                onChange={(e) => updateField('licensePlate', e.target.value.toUpperCase())}
                className={`mt-1 block w-full rounded-md border ${
                  errors.licensePlate ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
                placeholder="ABC1234"
              />
              {errors.licensePlate && (
                <p className="mt-1 text-sm text-red-500">{errors.licensePlate}</p>
              )}
            </div>
          </div>

          {/* DOT Inspection Dates */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                DOT Inspection Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.dotInspectionDate}
                onChange={(e) => updateField('dotInspectionDate', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.dotInspectionDate ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.dotInspectionDate && (
                <p className="mt-1 text-sm text-red-500">{errors.dotInspectionDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                DOT Expiration Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.dotExpirationDate}
                onChange={(e) => updateField('dotExpirationDate', e.target.value)}
                className={`mt-1 block w-full rounded-md border ${
                  errors.dotExpirationDate ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500`}
              />
              {errors.dotExpirationDate && (
                <p className="mt-1 text-sm text-red-500">{errors.dotExpirationDate}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Additional notes..."
            />
            {errors.notes && <p className="mt-1 text-sm text-red-500">{errors.notes}</p>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{errors.submit}</div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Vehicle'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
